"use client";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import DatePicker from "../../../../components/DatePicker";
import MedicalHistoryHeader from "../components/MedicalHistoryHeader";
import styles from "../css/TreatmentHistory.module.css";
import { getConsultationHistoryPatient, getPatientConsultationTimeline } from "../../../../api/clinic/consultationApi.js";
import { getProfileByPatient } from "../../../../api/profile/create_profile.js";

export default function TreatmentHistory() {
  const { id } = useParams();
  const { consultationData } = useConsultation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [mainComplaint, setMainComplaint] = useState("");
  const [symptoms, setSymptoms] = useState("");
  const [examination, setExamination] = useState("");
  const [prescriptions, setPrescriptions] = useState("");
  const [vitals, setVitals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [patient, setPatient] = useState(consultationData?.patient || null);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setError("");
      if (consultationData?.patient) {
        if (mounted) setPatient(consultationData.patient);
      }
      if (!id) return;
      try {
        const p = await getConsultationHistoryPatient(id);
        if (mounted) setPatient(p);

        if (!p) {
          const t = await getPatientConsultationTimeline(id);
          const p2 = Array.isArray(t) && t.length ? t[0]?.patient : null;
          if (mounted) setPatient(p2 || null);
        }
      } catch {
        if (mounted) setPatient(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, consultationData?.patient]);

  useEffect(() => {
    const patientId = id;
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getProfileByPatient(patientId);
        if (!cancelled) setProfileData(data);
      } catch {
        if (!cancelled) setProfileData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const patientFromProfile = profileData?.patient || null;
  const resolvedPatient = {
    ...(patientFromProfile || patient || {}),
    profilenumber:
      profileData?.profile?.profilenumber ??
      patientFromProfile?.profilenumber ??
      patient?.profilenumber,
    medicalaidprovider: profileData?.mainmember?.medicalaidprovider ?? profileData?.mainmember?.medicalAidProvider,
    medicalaidnumber: profileData?.mainmember?.medicalaidnumber ?? profileData?.mainmember?.medicalAidNumber,
  };

  const dateKey = (val) => {
    if (!val) return "";
    if (typeof val === "string") {
      const s = val.trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const d2 = new Date(s);
      if (Number.isNaN(d2.getTime())) return "";
      const y = d2.getFullYear();
      const m = String(d2.getMonth() + 1).padStart(2, "0");
      const d = String(d2.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    const d = val instanceof Date ? val : new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) return;
      setLoading(true);
      setError("");
      try {
        const t = await getPatientConsultationTimeline(id);
        if (!mounted) return;
        const rows = Array.isArray(t) ? t : [];
        setTimeline(rows);
        if (!selectedDate && rows.length) {
          const d = rows[0]?.consultationdate ? new Date(rows[0].consultationdate) : new Date();
          setSelectedDate(d);
        }
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load treatment history");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const selectedConsultation = (() => {
    if (!selectedDate) return timeline[0] || null;
    const targetKey = dateKey(selectedDate);
    if (!targetKey) return timeline[0] || null;
    for (const c of timeline) {
      const ck = dateKey(c?.consultationdate);
      if (!ck) continue;
      if (ck === targetKey) {
        return c;
      }
    }
    return null;
  })();

  useEffect(() => {
    const c = selectedConsultation;
    if (!c) {
      setMainComplaint("");
      setSymptoms("");
      setExamination("");
      setVitals(null);
      setPrescriptions("");
      return;
    }
    const complaint = c?.consultation_complaint || {};
    setMainComplaint(complaint?.maincomplaint || "");
    setSymptoms(complaint?.symptomsreported || "");
    setExamination(complaint?.examinationfindings || "");

    const v = c?.consultation_vitals || null;
    setVitals(v);

    const lines = (c?.prescriptions || [])
      .flatMap((p) => {
        const drugs = Array.isArray(p?.prescriptiondrug) ? p.prescriptiondrug : [];
        return drugs.map((d) => {
          const activeIngredient = d?.drug?.activeingredient || "";
          const sku = d?.drugsku || d?.itemsku || "";
          const qty = d?.quantity != null ? String(d.quantity) : "";
          const directions = d?.directions || "";
          const icd10 = d?.icd10 || "";
          const parts = [
            activeIngredient || sku,
            qty ? `x${qty}` : "",
            directions,
            icd10 ? `ICD10:${icd10}` : "",
          ]
            .filter(Boolean)
            .join(" ")
            .trim();
          return parts;
        });
      })
      .filter(Boolean);

    setPrescriptions(lines.length ? lines.join("\n") : "");
  }, [selectedConsultation]);

  return (
    <div className={styles.pageWrapper}>
      <Navbar />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          <MedicalHistoryHeader title="Treatment History" patient={resolvedPatient} />

          {/* Date Selector */}
          <div className={styles.dateSelector}>
            
            <div className={styles.datePickerContainer}>
              <DatePicker
                selectedDate={selectedDate || new Date()}
                onDateChange={setSelectedDate}
                dateFormat="dd/MM/yyyy"
                placeholder="Select date"
              />
            </div>
          </div>

          {error && <div className={styles.inlineError}>{error}</div>}

          {loading ? (
            <div className={styles.loadingMessage}>Loading...</div>
          ) : null}

          {!loading && !error && selectedDate && timeline.length && !selectedConsultation ? (
            <div className={styles.inlineError}>No consultation found for the selected date.</div>
          ) : null}

          {/* Treatment Information Cards */}
          <div className={styles.cardsContainer}>
            {/* Main Complaint - Full Width */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>MAIN COMPLAINT</h3>
              </div>
              <div className={styles.cardContent}>
                <textarea
                  className={styles.textArea}
                  value={mainComplaint}
                  onChange={(e) => setMainComplaint(e.target.value)}
                 
                />
              </div>
            </div>

            {/* Symptoms Reported and Physical Examination Findings - Side by Side */}
            <div className={styles.twoColumnRow}>
              {/* Symptoms Reported */}
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>SYMPTOMS REPORTED</h3>
                </div>
                <div className={styles.cardContent}>
                  <textarea
                    className={styles.textArea}
                    value={symptoms}
                    onChange={(e) => setSymptoms(e.target.value)}
                   
                  />
                </div>
              </div>

              {/* Physical Examination Findings */}
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>PHYSICAL EXAMINATION FINDINGS</h3>
                </div>
                <div className={styles.cardContent}>
                  <textarea
                    className={styles.textArea}
                    value={examination}
                    onChange={(e) => setExamination(e.target.value)}
                 
                  />
                </div>
              </div>
            </div>

            {/* Vitals Summary and Prescriptions - Side by Side */}
            <div className={styles.twoColumnRow}>
              {/* Vitals Summary */}
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>VITALS SUMMARY</h3>
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.vitalsGrid}>
                    <div className={styles.vitalItem}>
                      <span className={styles.vitalLabel}>BP:</span>
                      <span className={styles.vitalValue}>
                        {vitals?.bpsystolic != null || vitals?.bpdiastolic != null
                          ? `${vitals?.bpsystolic ?? ""}/${vitals?.bpdiastolic ?? ""}`
                          : "—"}
                      </span>
                    </div>
                    <div className={styles.vitalItem}>
                      <span className={styles.vitalLabel}>BMI:</span>
                      <span className={styles.vitalValue}>{vitals?.bmi ?? "—"}</span>
                    </div>
                    <div className={styles.vitalItem}>
                      <span className={styles.vitalLabel}>Temp:</span>
                      <span className={styles.vitalValue}>
                        {vitals?.temperature != null ? `${vitals.temperature}°C` : "—"}
                      </span>
                    </div>
                    <div className={styles.vitalItem}>
                      <span className={styles.vitalLabel}>Glucose:</span>
                      <span className={styles.vitalValue}>{vitals?.glucose ?? "—"}</span>
                    </div>
                    <div className={styles.vitalItem}>
                      <span className={styles.vitalLabel}>Pulse:</span>
                      <span className={styles.vitalValue}>
                        {vitals?.pulse != null ? `${vitals.pulse}/min` : "—"}
                      </span>
                    </div>
                    <div className={styles.vitalItem}>
                      <span className={styles.vitalLabel}>Weight:</span>
                      <span className={styles.vitalValue}>
                        {vitals?.weight != null ? `${vitals.weight}kg` : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prescriptions */}
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                 
                </div>
                <div className={styles.cardContent}>
                  <textarea
                    className={`${styles.textArea} ${styles.prescriptionsTextArea}`}
                    value={prescriptions}
                    onChange={(e) => setPrescriptions(e.target.value)}
                    placeholder="Enter prescriptions..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
